/**
 * B3 · Fluxo de Investidores (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_b3_fluxo.py).
 */
import { Alert, Loader, SimpleGrid, Tabs } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { AvisosApi } from '../../components/AvisosApi';
import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { BarraFiltros, CampoOpcoes, GrupoFiltros } from '../../components/Filtros';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { opcoesPeriodo } from '../../lib/periodos';
import { useFiltrosUrl } from '../../lib/url-state';

interface DadosB3Fluxo {
  periodo: string;
  periodos: { valor: string; rotulo: string }[];
  kpis: KpiApi[];
  abas: { titulo: string; graficos: GraficoApi[] }[];
}

// Mesmos períodos do legado; usados enquanto a primeira resposta não chega.
const PERIODOS_INICIAIS = opcoesPeriodo(['ytd', '30d', '6m', '1a', '2a']);

export function B3Fluxo() {
  const [filtros, definir] = useFiltrosUrl({ periodo: 'ytd' });
  const consulta = useQuery({
    queryKey: ['equities.b3_fluxo', filtros.periodo],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosB3Fluxo>>('/equities/b3-fluxo-de-investidores', filtros, signal),
    placeholderData: keepPreviousData, // troca de período sem piscar a página
  });
  const dados = consulta.data?.data;
  const periodos = dados?.periodos ?? PERIODOS_INICIAIS;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="B3 · Fluxo de Investidores"
        descricao="Fluxo de investimento na bolsa por tipo de investidor: saldo do mês e do ano, saldo diário e acumulado por ano."
        dadosAte={consulta.data?.meta.dados_ate}
        fonte="B3"
      />
      <BarraFiltros carregando={consulta.isFetching}>
        <GrupoFiltros titulo="Saldo diário">
          <CampoOpcoes
            rotulo="Período"
            valor={filtros.periodo}
            aoMudar={(periodo) => definir({ periodo })}
            opcoes={periodos}
          />
        </GrupoFiltros>
      </BarraFiltros>

      {consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi
            ? consulta.error.message
            : 'Não foi possível carregar os dados.'}
        </Alert>
      )}

      {consulta.isPending && <Loader />}

      {dados && (
        <>
          <AvisosApi avisos={[consulta.data?.meta.avisos]} />

          <Secao titulo="Indicadores chave" legenda="Saldo no mês corrente e, abaixo, no ano.">
            <SimpleGrid cols={{ base: 1, sm: 3, lg: 5 }}>
              {dados.kpis.map((kpi) => (
                <Kpi key={kpi.rotulo} kpi={kpi} />
              ))}
            </SimpleGrid>
          </Secao>

          <Secao
            titulo="Fluxo por tipo de investidor"
            legenda="Saldo diário e acumulado no período escolhido (à esquerda) e acumulado por ano (à direita)."
          >
            <Tabs defaultValue={dados.abas[0]?.titulo} keepMounted={false} color="dourado">
              <Tabs.List>
                {dados.abas.map((aba) => (
                  <Tabs.Tab key={aba.titulo} value={aba.titulo}>
                    {aba.titulo}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              {dados.abas.map((aba) => (
                <Tabs.Panel key={aba.titulo} value={aba.titulo} pt="md">
                  <SimpleGrid cols={{ base: 1, lg: 2 }}>
                    {aba.graficos.map((grafico) => (
                      <Grafico key={grafico.id} grafico={grafico} />
                    ))}
                  </SimpleGrid>
                </Tabs.Panel>
              ))}
            </Tabs>
          </Secao>
        </>
      )}
    </Pagina>
  );
}
